<template>
  <div class="fts-pagination">
    <!-- summary -->
    <div class="pagination-summary">
      {{currentItemIndex}}
      <template v-if="pagination">of {{isTotalItemsExceed ? `${getTotalItemsLimit - 1}+` : totalItems}}</template>
    </div>
    <!-- control -->
    <div class="pagination-control">
      <div class="pagination-container">
        <!-- first page -->
        <div
          class="page-item"
          :class="{ 'page-item-disabled': isFirstPage }"
          @click="firstPage"
        >
          First
        </div>
        <template v-if="pagination">
          <!-- pages -->
          <template v-for="page in pageNumbers">
            <div
              :key="page"
              class="page-item"
              :class="{ 'page-item-active': currentPage === page }"
              @click="setCurrentPage(page)"
              v-if="page >= 0"
            >
              {{page + 1}}
            </div>
          </template>
          <!-- next page -->
          <div
            class="page-item"
            :class="{ 'page-item-disabled': isLastPage }"
            key="next-page"
            @click="!isLastPage && nextPage()"
            v-if="isTotalItemsExceed"
          >
            Next
          </div>
          <!-- last page -->
          <div
            class="page-item"
            :class="{ 'page-item-disabled': isLastPage }"
            key="last-page"
            @click="lastPage"
            v-else
          >
            Last
          </div>
        </template>
        <!-- next page (pagination not available) -->
        <div
          class="page-item"
          key="next-page-without-pagination"
          @click="nextPage"
          v-else
        >
          Next
        </div>
      </div>
      <!-- page size selection -->
      <fts-dropdown-select
        append-toggle-class="pagination-dropdown-toggle"
        placement="top-end"
        :options="pageList" 
        v-model="currentPageSize"
        v-if="pagination"
      />
    </div>
  </div>
</template>

<script>
import commons from '../commons/commons';
import { Listing_Max_Total_Entries } from '../commons/flags';
import FTSDropdownSelect from "./FTSDropdownSelect";

export default {
  props: {
    pagination: {
      type: Boolean,
      default: true,
    },
    totalItems: {
      type: Number,
      default: 0
    },
    totalItemsLimit: Number,
    pageSize: {
      type: Number,
      default: 20
    },
    limit: Number,
    // Lets the parent drive the active page (e.g. reset to the first page when a
    // new search reloads the data). Null keeps the component self-managed, so
    // existing consumers that don't bind it are unaffected.
    page: {
      type: Number,
      default: null
    }
  },
  model: {
    prop: "pageSize",
    event: "updatePageSize",
  },
  data: () => ({
    currentPage: 0,
    pageList: [ 10, 20, 50, 100, 200 ],
  }),
  components: {
    "fts-dropdown-select": FTSDropdownSelect,
  },
  methods: {
    setCurrentPage(page) {
      this.currentPage = page;
    },
    firstPage() {
      this.currentPage = 0;
    },
    lastPage() {
      this.currentPage = Math.ceil(this.totalItems / this.pageSize) - 1;
    },
    nextPage() {
      this.currentPage = this.currentPage + 1;
    }
  },
  watch: {
    currentPage: function() {
      // A sync driven by the parent's `page` prop must not echo pageChanged back
      // (the parent already reloads); only user-driven page changes emit.
      if (this._suppressPageEmit) {
        this._suppressPageEmit = false;
        return;
      }
      this.$emit('pageChanged', this.currentPage);
    },
    pageSize: function() {
      if ((this.currentPage * this.pageSize) + 1 > this.totalItems) {
        this.currentPage = Math.max(Math.ceil(this.totalItems / this.pageSize) - 1 ,0);
      } else {
        this.$emit('pageChanged', this.currentPage);
      }
    },
    totalItems: function() {
      this.currentPage = 0;
    },
    page: function(val) {
      // Parent-driven active page (e.g. search reset to page 1). Move the
      // highlight to match even when totalItems is unchanged — a wildcard "*"
      // returns every row, so the totalItems watcher above never fires (SS05910).
      if (val === null || val === this.currentPage) return;
      this._suppressPageEmit = true;
      this.currentPage = val;
    }
  },
  computed: {
    currentItemIndex() {
      const startItemIndex = (this.currentPage * this.pageSize) + 1;
      const endItemIndex = Math.min((this.currentPage + 1) * this.pageSize, this.totalItems);
      return `${startItemIndex} - ${endItemIndex}`;
    },
    pageNumbers() {
      const totalPage = Math.ceil(this.totalItems / this.pageSize);
      let pageNumbers = [];
      let startPage1 = Math.max(this.currentPage - 2, 0);
      let startPage2 = Math.max(totalPage - 5, 0);
      let startPage = Math.min(startPage1, startPage2);

      for (var i = 0; i < 5; i++) {
        if(startPage + i < totalPage) {
          pageNumbers.push(startPage + i);
        }
      }

      return pageNumbers;
    },
    currentPageSize: {
      get() {
        return this.pageSize;
      },
      set(val) {
        this.$emit('updatePageSize', val);
        this.$emit('page-size-changed', val);
      },
    },
    isFirstPage() {
      return this.currentPage === 0;
    },
    isLastPage() {
      return Boolean(this.currentPage === Math.ceil(this.totalItems / this.pageSize) - 1);
    },
    getTotalItemsLimit() {
      if (this.totalItemsLimit && commons.isInteger(this.totalItemsLimit) && this.totalItemsLimit > 0) {
        return this.totalItemsLimit;
      }

      return Listing_Max_Total_Entries;
    },
    isTotalItemsExceed() {
      return this.totalItems >= this.getTotalItemsLimit;
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;
@use "./src/assets/scss/colors" as *;

.fts-pagination {
  @include user-select-none;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  font-size: var(--inistate-label-text-14);
  line-height: var(--inistate-paragraph-text-14--line-height);
  color: var(--fts-text);
  .pagination-summary {
    margin-right: 1rem;
    padding: 0.375rem;
  }
  .pagination-control {
    display: flex;
    align-items: center;
    .pagination-container {
      display: flex;
      flex-wrap: wrap;
      align-items: center;
      .page-item {
        padding: .375rem .575rem;
        &:hover {
          &:not(.page-item-disabled) {
            @include theme() {
              color: rgba($color: theme-get('fts-primary'), $alpha: 0.85);
            }
          }   
          cursor: pointer;
        }
        &.page-item-active {
          color: var(--fts-primary);
        }
        &.page-item-disabled {
          color: var(--fts-disabled);
          cursor: not-allowed;
        }
      }
    }  
    .pagination-dropdown-toggle {
      padding: .375rem .75rem; 
    }
  }
}
</style>